import type { SidebarItem, SidebarSubItem } from '@UI/Menus/SidebarMenu'

import { OrderStatus } from '@brighterly/lib-core-types/orders/enums'
import { AppRoute } from '@ts-routes/enums'

import { useListsStore } from '@stores/lists'

import { useAdminRoutes } from '@composables/routes/admin-route.hook'
import { useSidebarPrivileges } from '@hooks/sidebar/sidebar-privileges.hook'

import {
  faBookOpen,
  faGear,
  faGraduationCap,
  faPuzzlePiece,
  faScrewdriverWrench,
  faUserGroup,
  faUsers,
  faFileLines,
  faFolderOpen,
  faChartPieSimple,
  faCalendar,
  faFileInvoiceDollar,
} from '@fortawesome/pro-regular-svg-icons'

interface UseSidebarItemsReturn {
  refItems: ComputedRef<SidebarItem[]>
}

/** The sidebar opens Orders on the statuses support works with; a bare `/orders` shows every status. */
const DEFAULT_ORDER_STATUSES: OrderStatus[] = [
  OrderStatus.PROCESSING,
  OrderStatus.SUCCEEDED,
  OrderStatus.FAILED,
  OrderStatus.REFUNDED,
]

/** A prefix may be an `AppRoute` template: each `[param]` segment matches any one path segment. */
const isActivePath = (path: string, prefixes: string[]): boolean =>
  prefixes.some(prefix => new RegExp(`^${prefix.replace(/\[[^\]]+\]/g, '[^/]+')}(/|$)`).test(path))

export const useSidebarItems = (): UseSidebarItemsReturn => {
  const route = useRoute()
  const router = useRouter()
  const listsStore = useListsStore()
  const privileges = useSidebarPrivileges()

  const isTasksKanbanEnabled = computed(() => listsStore.getLists?.tasks_kanban?.enabled || false)

  // ---- old admin (main-app) external URLs (static — built once per hook setup) ----
  const kanbanServiceUrl = useAdminRoutes('admin.kanban.service') ?? ''

  const attendanceSetterUrl = useAdminRoutes('admin.attendance-setter.index', {
    page: 1,
    sort_by: 'start',
    sort_direction: 'DESC',
  }) ?? ''
  const demoSalesUrl = useAdminRoutes('admin.demo-sale.lessons.index', {
    page: 1,
    mode: 'COMPLETED',
    sort_by: 'sale_score',
    sort_direction: 'DESC',
  }) ?? ''

  const paidLessonsUrl = useAdminRoutes('admin.paid-lessons.index') ?? ''
  const demoLessonsUrl = useAdminRoutes('admin.demo-lessons.index') ?? ''

  const ordersPath = router.resolve({ path: AppRoute.Orders, query: { status: DEFAULT_ORDER_STATUSES } }).fullPath

  const tutorsUrl = useAdminRoutes('admin.tutors.index') ?? ''
  const tutorPaymentsUrl = useAdminRoutes('admin.tutor-payments.index') ?? ''
  const tutorPaymentPoliciesUrl = useAdminRoutes('admin.tutors.payment-policies.index') ?? ''
  const tutorScheduleUrl = useAdminRoutes('admin.tutor-schedule.day') ?? ''
  const tutorTasksUrl = useAdminRoutes('admin.tutor-tasks.index', {
    status: ['PLANNED'],
  }) ?? ''

  const newslettersUrl = useAdminRoutes('admin.newsletters.index') ?? ''
  const newsletterLogsUrl = useAdminRoutes('admin.newsletter-logs.index') ?? ''

  // ---- active state helpers ----

  // A group is active while one of its children matches the route; external children never match.
  const isAnyChildActive = (children: SidebarSubItem[]): boolean =>
    children.some(child => child.active)

  // ---- children ----
  const customersChildren = computed<SidebarSubItem[]>(() => [
    {
      id: 'customers',
      text: 'Customers',
      to: AppRoute.Customers,
      active: isActivePath(route.path, [
        AppRoute.Customers,
        AppRoute.KidsShow,
        AppRoute.DisciplinesGeneralInfo,
        AppRoute.BookingsChangelog,
      ]),
      visible: privileges.canViewCustomers.value,
    },
    {
      id: 'survey',
      text: 'Surveys',
      to: AppRoute.Surveys,
      active: isActivePath(route.path, [AppRoute.Surveys]),
      visible: privileges.canViewSurveys.value,
    },
    {
      id: 'kanban',
      text: 'Kanban',
      to: kanbanServiceUrl,
      active: false,
      visible: privileges.canViewKanban.value,
      isExternal: true,
    },
    {
      id: 'tasks-kanban',
      text: 'Tasks kanban',
      to: AppRoute.TasksKanbanSupport,
      active: isActivePath(route.path, [
        AppRoute.TasksKanbanSupport,
        AppRoute.TasksKanbanSuccess,
        AppRoute.TasksKanbanBilling,
        AppRoute.TasksKanbanCreation,
      ]),
      visible: isTasksKanbanEnabled.value,
    },
  ])

  const demosChildren = computed<SidebarSubItem[]>(() => [
    {
      id: 'attendance-setter',
      text: 'Attendance setter',
      to: attendanceSetterUrl,
      active: false,
      visible: false,
      isExternal: true,
    },
    {
      id: 'slots-tracker',
      text: 'Shift control',
      to: AppRoute.DemosSlotsTracker,
      active: isActivePath(route.path, [AppRoute.DemosSlotsTracker]),
      visible: true,
    },
    {
      id: 'demo-sales',
      text: 'Sales',
      to: demoSalesUrl,
      active: false,
      visible: true,
      isExternal: true,
    },
    {
      id: 'demo-slots-management',
      text: 'Slots management',
      to: AppRoute.DemosSlotsManagement,
      active: isActivePath(route.path, [AppRoute.DemosSlotsManagement]),
      visible: privileges.canViewSlotsManagement.value,
    },
    {
      id: 'extra-rates',
      text: 'Extra rates',
      to: AppRoute.DemosExtraRates,
      active: isActivePath(route.path, [AppRoute.DemosExtraRates]),
      visible: true,
    },
  ])

  const lessonsChildren = computed<SidebarSubItem[]>(() => [
    {
      id: 'paid-lessons',
      text: 'Paid Lessons',
      to: paidLessonsUrl,
      active: false,
      visible: privileges.canViewPaidBookings.value,
      isExternal: true,
    },
    {
      id: 'demo-lessons',
      text: 'Demo Lessons',
      to: demoLessonsUrl,
      active: false,
      visible: privileges.canViewDemoBookings.value,
      isExternal: true,
    },
  ])

  const ordersChildren = computed<SidebarSubItem[]>(() => [
    {
      id: 'orders',
      text: 'Orders',
      to: ordersPath,
      active: isActivePath(route.path, [AppRoute.Orders]),
      visible: privileges.canViewOrders.value,
    },
    {
      id: 'subscriptions',
      text: 'Subscriptions',
      to: AppRoute.Subscriptions,
      active: isActivePath(route.path, [AppRoute.Subscriptions]),
      visible: privileges.canViewSubscriptions.value,
    },
    {
      id: 'promo-codes',
      text: 'Promo codes',
      to: AppRoute.Promocodes,
      active: isActivePath(route.path, [AppRoute.Promocodes]),
      visible: privileges.canViewPromocodes.value,
    },
  ])

  const tutorsChildren = computed<SidebarSubItem[]>(() => [
    {
      id: 'tutors',
      text: 'Tutors',
      to: tutorsUrl,
      active: false,
      visible: privileges.canViewTutors.value,
      isExternal: true,
    },
    {
      id: 'payment-policy',
      text: 'Payment policies',
      to: tutorPaymentPoliciesUrl,
      active: false,
      visible: privileges.canViewTutorPaymentPolicies.value || privileges.canManageTutorPaymentPolicies.value,
      isExternal: true,
    },
    {
      id: 'tutors-schedule',
      text: 'Tutors schedule',
      to: tutorScheduleUrl,
      active: false,
      visible: privileges.canViewTutorsSchedule.value,
      isExternal: true,
    },
    {
      id: 'tutors-tasks',
      text: 'Tutors tasks',
      to: tutorTasksUrl,
      active: false,
      visible: privileges.canViewTutorTasks.value,
      isExternal: true,
    },
    {
      id: 'tutors-attendance-rates',
      text: 'Tutor attendance rates',
      to: AppRoute.TutorAttendanceRates,
      active: isActivePath(route.path, [AppRoute.TutorAttendanceRates]),
      visible: privileges.canViewTutors.value,
    },
    {
      id: 'tutor-kanban',
      text: 'Tutor kanban',
      to: AppRoute.TutorKanbanOnboarding,
      active: isActivePath(route.path, [
        AppRoute.TutorKanbanOnboarding,
        AppRoute.TutorKanbanActive,
        AppRoute.TutorKanbanProbation,
      ]),
      visible: privileges.canViewTutorKanban.value,
    },
  ])

  const tutorsGroupUrl = computed<string>(() => {
    if (privileges.canViewTutors.value) return tutorsUrl
    if (privileges.canViewTutorsSchedule.value) return tutorScheduleUrl

    return tutorTasksUrl
  })

  const billingChildren = computed<SidebarSubItem[]>(() => [
    {
      id: 'tutor-billing-info',
      text: 'Tutor billing info',
      to: AppRoute.BillingTutorInfo,
      active: isActivePath(route.path, [AppRoute.BillingTutorInfo]),
      visible: privileges.canManageTutorBilling.value,
    },
    {
      id: 'tutor-payments',
      text: 'Tutor payments',
      to: tutorPaymentsUrl,
      active: false,
      visible: privileges.canViewTutorPayments.value,
      isExternal: true,
    },
  ])

  // Menu labels only: routes, page titles and the code keep "assessment" / "assignment" templates.
  const contentChildren = computed<SidebarSubItem[]>(() => [
    {
      id: 'assessment-templates',
      text: 'Assessments',
      to: AppRoute.Assessments,
      active: isActivePath(route.path, [AppRoute.Assessments]),
      visible: privileges.canViewAssessmentTemplates.value,
    },
    {
      id: 'assignment-templates',
      text: 'Homework',
      to: AppRoute.Assignments,
      active: isActivePath(route.path, [AppRoute.Assignments]),
      visible: privileges.canViewAssignmentTemplates.value,
    },
    {
      id: 'stories',
      text: 'Stories',
      to: AppRoute.Stories,
      active: isActivePath(route.path, [AppRoute.Stories]),
      visible: privileges.canViewAssignmentTemplates.value,
    },
  ])

  const newslettersChildren = computed<SidebarSubItem[]>(() => [
    {
      id: 'newsletters',
      text: 'Newsletters',
      to: newslettersUrl,
      active: false,
      visible: privileges.canViewNewsletters.value,
      isExternal: true,
    },
    {
      id: 'newsletter-logs',
      text: 'Newsletter logs',
      to: newsletterLogsUrl,
      active: false,
      visible: privileges.canViewNewsletterLogs.value,
      isExternal: true,
    },
  ])

  const usersChildren = computed<SidebarSubItem[]>(() => [
    {
      id: 'users',
      text: 'Users & teams',
      to: AppRoute.Users,
      active: isActivePath(route.path, [AppRoute.Users]),
      visible: privileges.canViewUsers.value,
    },
    {
      id: 'api-tokens',
      text: 'API Tokens',
      to: AppRoute.ApiTokens,
      active: isActivePath(route.path, [AppRoute.ApiTokens]),
      visible: privileges.canManageApiTokens.value,
    },
  ])

  const adminChildren = computed<SidebarSubItem[]>(() => [
    {
      id: 'settings',
      text: 'Settings',
      to: AppRoute.Settings,
      active: isActivePath(route.path, [AppRoute.Settings, AppRoute.SettingsExperiments]),
      visible: privileges.canViewSettings.value,
    },
    {
      id: 'oauth',
      text: 'OAuth',
      to: AppRoute.OAuth,
      active: isActivePath(route.path, [AppRoute.OAuth]),
      visible: privileges.canViewSettings.value,
    },
    {
      id: 'changelog',
      text: 'Changelog',
      to: AppRoute.Changelog,
      active: isActivePath(route.path, [AppRoute.Changelog]),
      visible: privileges.canViewChangelog.value,
    },
    {
      id: 'helpers',
      text: 'Helpers',
      to: AppRoute.Helpers,
      active: isActivePath(route.path, [AppRoute.Helpers]),
      visible: privileges.canViewSettings.value,
    },
  ])

  const toolsChildren = computed<SidebarSubItem[]>(() => [
    {
      id: 'import-sales-tax',
      text: 'Import SalesTax',
      to: AppRoute.ImportSalesTax,
      active: isActivePath(route.path, [AppRoute.ImportSalesTax]),
      visible: privileges.canImportSalesTax.value,
    },
    {
      id: 'import-curriculum',
      text: 'Import Curriculum',
      to: AppRoute.ImportCurriculum,
      active: isActivePath(route.path, [AppRoute.ImportCurriculum]),
      visible: privileges.canImportCurriculum.value,
    },
    {
      id: 'import-tutor-payments',
      text: 'Import Tutor Payments',
      to: AppRoute.ImportTutorPayments,
      active: isActivePath(route.path, [AppRoute.ImportTutorPayments]),
      visible: privileges.canManageTutorPayments.value,
    },
  ])

  const refItems = computed<SidebarItem[]>(() => [
    {
      id: 'dashboard',
      text: 'Dashboard',
      to: AppRoute.Homepage,
      icon: faChartPieSimple,
      active: isActivePath(route.path, [AppRoute.Homepage]),
      visible: true,
    },
    {
      id: 'customers',
      text: 'Customers',
      to: AppRoute.Customers,
      icon: faUsers,
      active: isAnyChildActive(customersChildren.value),
      visible: privileges.canViewCustomers.value || privileges.canSimpleViewDisciplines.value,
      children: customersChildren.value,
    },
    {
      id: 'demos',
      text: 'Demos',
      to: AppRoute.DemosSlotsTracker,
      icon: faPuzzlePiece,
      active: isAnyChildActive(demosChildren.value),
      visible: privileges.canViewDemoBookings.value,
      children: demosChildren.value,
    },
    {
      id: 'lessons',
      text: 'Lessons',
      to: paidLessonsUrl,
      icon: faCalendar,
      active: isAnyChildActive(lessonsChildren.value),
      visible: privileges.canViewPaidBookings.value || privileges.canViewDemoBookings.value,
      isExternal: true,
      children: lessonsChildren.value,
    },
    {
      id: 'orders',
      text: 'Orders',
      to: ordersPath,
      icon: faFileLines,
      active: isAnyChildActive(ordersChildren.value),
      visible: privileges.canViewOrders.value,
      children: ordersChildren.value,
    },
    {
      id: 'tutors',
      text: 'Tutors',
      to: tutorsGroupUrl.value,
      icon: faGraduationCap,
      active: isAnyChildActive(tutorsChildren.value),
      visible: privileges.canViewTutors.value || privileges.canViewTutorsSchedule.value || privileges.canViewTutorTasks.value,
      isExternal: true,
      children: tutorsChildren.value,
    },
    {
      id: 'billing',
      text: 'Billing & Payments',
      to: privileges.canManageTutorBilling.value ? AppRoute.BillingTutorInfo : tutorPaymentsUrl,
      icon: faFileInvoiceDollar,
      active: isAnyChildActive(billingChildren.value),
      visible: billingChildren.value.some(child => child.visible),
      isExternal: !privileges.canManageTutorBilling.value,
      children: billingChildren.value,
    },
    {
      id: 'content',
      text: 'Content',
      to: privileges.canViewAssessmentTemplates.value ? AppRoute.Assessments : AppRoute.Assignments,
      icon: faFolderOpen,
      active: isAnyChildActive(contentChildren.value),
      visible: privileges.canViewAssessmentTemplates.value || privileges.canViewAssignmentTemplates.value,
      children: contentChildren.value,
    },
    {
      id: 'newsletters',
      text: 'Newsletters',
      to: privileges.canViewNewsletters.value ? newslettersUrl : newsletterLogsUrl,
      icon: faBookOpen,
      active: isAnyChildActive(newslettersChildren.value),
      visible: privileges.canViewNewsletterLogs.value,
      isExternal: true,
      children: newslettersChildren.value,
    },
    {
      id: 'users',
      text: 'Users & teams',
      to: AppRoute.Users,
      icon: faUserGroup,
      active: isAnyChildActive(usersChildren.value),
      visible: privileges.canViewUsers.value || privileges.canManageApiTokens.value,
      children: usersChildren.value,
    },
    {
      id: 'admin',
      text: 'Admin',
      to: AppRoute.Settings,
      icon: faGear,
      active: isAnyChildActive(adminChildren.value),
      visible: privileges.canViewSettings.value || privileges.canViewChangelog.value,
      children: adminChildren.value,
    },
    {
      id: 'tools',
      text: 'Tools',
      to: AppRoute.ImportSalesTax,
      icon: faScrewdriverWrench,
      active: isAnyChildActive(toolsChildren.value),
      visible: privileges.canImportSalesTax.value
        || privileges.canImportCurriculum.value
        || privileges.canManageTutorPayments.value,
      children: toolsChildren.value,
    },
  ])

  return {
    refItems,
  }
}
